import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectIsAuthenticated,
  selectIsOrgOwnerOrAdmin,
} from "../../authentication/authenticationSlice";
import {
  selectSampleDataActive,
  selectSampleDataFetchStatus,
  selectSampleDataStatus,
} from "../sample-data-slice";
import { fetchSampleDataStatus } from "../slice-extensions/sample-data-slice-extension";
import { LoadingStatus } from "../../sessions/models/LoadingStatus";
import { ROUTES } from "../../../routes";

/**
 * App-wide honesty layer: while sample data is loaded, every instructor sees
 * that the "Sample"-badged members and sessions are examples. Owners/Admins get
 * a link to the organization settings card that removes the set.
 */
const SampleDataBanner = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const status = useAppSelector(selectSampleDataStatus);
  const fetchStatus = useAppSelector(selectSampleDataFetchStatus);
  const active = useAppSelector(selectSampleDataActive);
  const isOrgAdmin = useAppSelector(selectIsOrgOwnerOrAdmin);

  useEffect(() => {
    if (isAuthenticated && status === null && fetchStatus === LoadingStatus.Idle) {
      dispatch(fetchSampleDataStatus());
    }
  }, [isAuthenticated, status, fetchStatus, dispatch]);

  if (!isAuthenticated || !active) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 bg-secondary-95 px-4 py-3 dark:bg-secondary-20">
      <p className="text-sm font-medium text-secondary-30 dark:text-secondary-80">
        {t(
          "sampleData.bannerText",
          "Sample data is loaded — entries marked “Sample” are examples to explore.",
        )}
      </p>
      {isOrgAdmin && (
        <Link
          to={ROUTES.ORGANIZATION_SETTINGS}
          className="text-sm font-semibold text-secondary-30 underline hover:no-underline dark:text-secondary-80"
        >
          {t("sampleData.bannerManage", "Manage sample data")}
        </Link>
      )}
    </div>
  );
};

export default SampleDataBanner;
